import {
  LayoutDashboard,
  Users,
  Clock,
  BarChart3,
  Monitor,
  Settings,
  Building,
  Calendar,
  FileText,
  Activity,
  Shield,
  TrendingUp,
  Server,
  Fingerprint,
  CalendarDays,
  FileCode,
  Calculator,
  PiggyBank,
  Sparkles,
  UserPlus,
  Target,
  FileSignature,
  Scale,
  Heart,
  Rocket,
  BookOpen,
  GitBranch,
  ShieldCheck,
  Gift,
  Receipt,
  KeyRound,
  MessageSquare,
  Plug,
  BadgeDollarSign,
  LogOut,
  Network,
  Star,
  Layers,
  Milestone,
  Smile,
  Briefcase,
  Zap,
  CalendarCheck,
  Home,
} from "lucide-react";

/**
 * Sidebar navigation, grouped by HR business domain.
 *
 * IMPORTANT:
 * - Every `path` is preserved verbatim; permission filtering keys off `path`
 *   (see lib/permissions.js → ROUTE_PERMISSIONS), so routes/permissions are
 *   unaffected by this grouping.
 * - All labels/descriptions come from i18n (sidebar.groups.* / sidebar.nav.*).
 * - Badges are limited to functional markers (Pro, Enterprise, IA/AI, SOC 2,
 *   RGPD, CNSS, ATS, CEO, Cyber, Setup, Secured, Analytics). Generic "New"
 *   badges were removed during the May 2026 reorg.
 */
export const buildMenuItems = (t) => [
  {
    key: "overview",
    title: t("sidebar.groups.overview"),
    icon: Home,
    items: [
      {
        key: "dashboard",
        title: t("sidebar.nav.dashboard"),
        icon: Home,
        path: "/dashboard",
        description: t("sidebar.nav.dashboardDesc"),
      },
      {
        key: "executive-dashboard",
        title: t("sidebar.nav.executiveDashboard"),
        icon: BarChart3,
        path: "/executive-dashboard",
        description: t("sidebar.nav.executiveDashboardDesc"),
        badge: "CEO",
        badgeVariant: "default",
      },
    ],
  },
  {
    key: "collaborators",
    title: t("sidebar.groups.collaborators"),
    icon: Users,
    // Ordered macro → micro: company → org structure → positions → people → tooling.
    // This mirrors the new-tenant setup flow (Informations Société carries the
    // "Setup" badge). Reordering only — paths/permissions are unchanged.
    items: [
      {
        key: "companyInfo",
        title: t("sidebar.nav.companyInfo"),
        icon: Building,
        path: "/company-configuration",
        description: t("sidebar.nav.companyInfoDesc"),
        badge: "Setup",
        badgeVariant: "default",
      },
      {
        key: "organigramme",
        title: t("sidebar.nav.organigramme"),
        icon: LayoutDashboard,
        path: "/organigramme",
        description: t("sidebar.nav.organigrammeDesc"),
      },
      {
        key: "positions",
        title: t("sidebar.nav.positions"),
        icon: Briefcase,
        path: "/positions",
        description: t("sidebar.nav.positionsDesc"),
      },
      {
        key: "employees",
        title: t("sidebar.nav.employees"),
        icon: Users,
        path: "/employees",
        description: t("sidebar.nav.employeesDesc"),
      },
      {
        key: "documentTemplates",
        title: t("sidebar.nav.documentTemplates"),
        icon: FileCode,
        path: "/document-templates",
        description: t("sidebar.nav.documentTemplatesDesc"),
      },
    ],
  },
  {
    key: "time",
    title: t("sidebar.groups.time"),
    icon: Clock,
    items: [
      {
        key: "planning",
        title: t("sidebar.nav.planning"),
        icon: Calendar,
        path: "/planning",
        description: t("sidebar.nav.planningDesc"),
      },
      {
        key: "rawAttendance",
        title: t("sidebar.nav.rawAttendance"),
        icon: Clock,
        path: "/pointages",
        description: t("sidebar.nav.rawAttendanceDesc"),
      },
      {
        key: "calculation",
        title: t("sidebar.nav.calculation"),
        icon: Calculator,
        path: "/pointages-advanced",
        description: t("sidebar.nav.calculationDesc"),
      },
      {
        key: "absences",
        title: t("sidebar.nav.absences"),
        icon: FileText,
        path: "/gestion-absences",
        description: t("sidebar.nav.absencesDesc"),
      },
      {
        key: "publicHolidays",
        title: t("sidebar.nav.publicHolidays"),
        icon: CalendarDays,
        path: "/jours-feries",
        description: t("sidebar.nav.publicHolidaysDesc"),
      },
      {
        key: "leave-policies",
        title: t("sidebar.nav.leavePolicies"),
        icon: CalendarDays,
        path: "/leave-policies",
        description: t("sidebar.nav.leavePoliciesDesc"),
      },
    ],
  },
  {
    key: "payroll",
    title: t("sidebar.groups.payroll"),
    icon: PiggyBank,
    items: [
      {
        key: "payroll",
        title: t("sidebar.nav.payroll"),
        icon: PiggyBank,
        path: "/paie",
        description: t("sidebar.nav.payrollDesc"),
      },
      {
        key: "compensation",
        title: t("sidebar.nav.compensation"),
        icon: BadgeDollarSign,
        path: "/compensation",
        description: t("sidebar.nav.compensationDesc"),
      },
      {
        key: "benefits",
        title: t("sidebar.nav.benefits"),
        icon: Gift,
        path: "/benefits",
        description: t("sidebar.nav.benefitsDesc"),
      },
      {
        key: "expenses",
        title: t("sidebar.nav.expenses"),
        icon: Receipt,
        path: "/expenses",
        description: t("sidebar.nav.expensesDesc"),
      },
    ],
  },
  {
    key: "recruitment",
    title: t("sidebar.groups.recruitment"),
    icon: UserPlus,
    items: [
      {
        key: "recrutement",
        title: t("sidebar.nav.recrutement"),
        icon: UserPlus,
        path: "/recrutement",
        description: t("sidebar.nav.recrutementDesc"),
        badge: "ATS",
        badgeVariant: "secondary",
      },
      {
        key: "interview-scheduling",
        title: t("sidebar.nav.interviewScheduling"),
        icon: CalendarCheck,
        path: "/interview-scheduling",
        description: t("sidebar.nav.interviewSchedulingDesc"),
      },
      {
        key: "esignature",
        title: t("sidebar.nav.esignature"),
        icon: FileSignature,
        path: "/esignature",
        description: t("sidebar.nav.esignatureDesc"),
      },
      {
        key: "onboarding",
        title: t("sidebar.nav.onboarding"),
        icon: Rocket,
        path: "/onboarding",
        description: t("sidebar.nav.onboardingDesc"),
      },
      {
        key: "offboarding",
        title: t("sidebar.nav.offboarding"),
        icon: LogOut,
        path: "/offboarding",
        description: t("sidebar.nav.offboardingDesc"),
      },
    ],
  },
  {
    key: "talent",
    title: t("sidebar.groups.talent"),
    icon: Target,
    items: [
      {
        key: "performance",
        title: t("sidebar.nav.performance"),
        icon: Target,
        path: "/performance",
        description: t("sidebar.nav.performanceDesc"),
      },
      {
        key: "one-on-one",
        title: t("sidebar.nav.oneOnOne"),
        icon: MessageSquare,
        path: "/one-on-one",
        description: t("sidebar.nav.oneOnOneDesc"),
      },
      {
        key: "recognition",
        title: t("sidebar.nav.recognition"),
        icon: Star,
        path: "/recognition",
        description: t("sidebar.nav.recognitionDesc"),
      },
      {
        key: "skills",
        title: t("sidebar.nav.skills"),
        icon: Layers,
        path: "/skills",
        description: t("sidebar.nav.skillsDesc"),
      },
      {
        key: "succession",
        title: t("sidebar.nav.succession"),
        icon: GitBranch,
        path: "/succession",
        description: t("sidebar.nav.successionDesc"),
      },
      {
        key: "career",
        title: t("sidebar.nav.career"),
        icon: Milestone,
        path: "/career",
        description: t("sidebar.nav.careerDesc"),
      },
      {
        key: "lms",
        title: t("sidebar.nav.lms"),
        icon: BookOpen,
        path: "/lms",
        description: t("sidebar.nav.lmsDesc"),
      },
      {
        key: "engagement",
        title: t("sidebar.nav.engagement"),
        icon: Heart,
        path: "/engagement",
        description: t("sidebar.nav.engagementDesc"),
      },
      {
        key: "wellbeing",
        title: t("sidebar.nav.wellbeing"),
        icon: Smile,
        path: "/wellbeing",
        description: t("sidebar.nav.wellbeingDesc"),
      },
    ],
  },
  {
    key: "analytics",
    title: t("sidebar.groups.analytics"),
    icon: TrendingUp,
    items: [
      {
        key: "workforce-planning",
        title: t("sidebar.nav.workforcePlanning"),
        icon: Network,
        path: "/workforce-planning",
        description: t("sidebar.nav.workforcePlanningDesc"),
      },
      {
        key: "analytics",
        title: t("sidebar.nav.analytics"),
        icon: TrendingUp,
        path: "/analytics",
        description: t("sidebar.nav.analyticsDesc"),
        badge: "AI",
        badgeVariant: "default",
      },
      {
        key: "basicReports",
        title: t("sidebar.nav.basicReports"),
        icon: FileText,
        path: "/reports",
        description: t("sidebar.nav.basicReportsDesc"),
      },
      {
        key: "advancedReports",
        title: t("sidebar.nav.advancedReports"),
        icon: BarChart3,
        path: "/advanced-reports",
        description: t("sidebar.nav.advancedReportsDesc"),
        badge: "Analytics",
        badgeVariant: "secondary",
      },
      {
        key: "report-builder",
        title: t("sidebar.nav.reportBuilder"),
        icon: BarChart3,
        path: "/report-builder",
        description: t("sidebar.nav.reportBuilderDesc"),
      },
    ],
  },
  {
    key: "ai",
    title: t("sidebar.groups.ai"),
    icon: Sparkles,
    items: [
      {
        key: "hrAnalyst",
        title: t("sidebar.nav.hrAnalyst"),
        icon: Sparkles,
        path: "/hr-analyst",
        description: t("sidebar.nav.hrAnalystDesc"),
        badge: "IA",
        badgeVariant: "default",
      },
      {
        key: "aiContractGenerator",
        title: t("sidebar.nav.aiContractGenerator"),
        icon: FileSignature,
        path: "/ai-contract-generator",
        description: t("sidebar.nav.aiContractGeneratorDesc"),
        badge: "IA",
        badgeVariant: "default",
      },
      {
        key: "workflow-automation",
        title: t("sidebar.nav.workflowAutomation"),
        icon: Zap,
        path: "/workflow-automation",
        description: t("sidebar.nav.workflowAutomationDesc"),
      },
    ],
  },
  {
    key: "infrastructure",
    title: t("sidebar.groups.infrastructure"),
    icon: Server,
    items: [
      {
        key: "terminals",
        title: t("sidebar.nav.terminals"),
        icon: Monitor,
        path: "/terminals",
        description: t("sidebar.nav.terminalsDesc"),
        badge: "Pro",
        badgeVariant: "outline",
      },
      {
        key: "monitoring",
        title: t("sidebar.nav.monitoring"),
        icon: Activity,
        path: "/zkteco-monitoring",
        description: t("sidebar.nav.monitoringDesc"),
      },
      {
        key: "biometricMapping",
        title: t("sidebar.nav.biometricMapping"),
        icon: Fingerprint,
        path: "/biometric-mapping",
        description: t("sidebar.nav.biometricMappingDesc"),
      },
    ],
  },
  {
    key: "compliance",
    title: t("sidebar.groups.compliance"),
    icon: Shield,
    items: [
      {
        key: "securityCenter",
        title: t("sidebar.nav.securityCenter"),
        icon: Shield,
        path: "/security-center",
        description: t("sidebar.nav.securityCenterDesc"),
        badge: "Cyber",
        badgeVariant: "destructive",
      },
      {
        key: "auditTrail",
        title: t("sidebar.nav.auditTrail"),
        icon: ShieldCheck,
        path: "/audit-trail",
        description: t("sidebar.nav.auditTrailDesc"),
        badge: "SOC 2",
        badgeVariant: "default",
      },
      {
        key: "compliance",
        title: t("sidebar.nav.compliance"),
        icon: Scale,
        path: "/compliance",
        description: t("sidebar.nav.complianceDesc"),
        badge: "CNSS",
        badgeVariant: "secondary",
      },
      {
        key: "gdpr",
        title: t("sidebar.nav.gdpr"),
        icon: ShieldCheck,
        path: "/gdpr",
        description: t("sidebar.nav.gdprDesc"),
        badge: "RGPD",
        badgeVariant: "secondary",
      },
      {
        key: "securityRoles",
        title: t("sidebar.nav.securityRoles"),
        icon: Shield,
        path: "/security-roles",
        description: t("sidebar.nav.securityRolesDesc"),
        badge: t("sidebar.badges.secured"),
        badgeVariant: "destructive",
      },
    ],
  },
  {
    key: "integrations",
    title: t("sidebar.groups.integrations"),
    icon: Plug,
    items: [
      {
        key: "sso",
        title: t("sidebar.nav.sso"),
        icon: KeyRound,
        path: "/sso",
        description: t("sidebar.nav.ssoDesc"),
        badge: "Enterprise",
        badgeVariant: "default",
      },
      {
        key: "integrations",
        title: t("sidebar.nav.integrations"),
        icon: Plug,
        path: "/integrations",
        description: t("sidebar.nav.integrationsDesc"),
      },
    ],
  },
  {
    key: "admin",
    title: t("sidebar.groups.admin"),
    icon: Settings,
    items: [
      {
        key: "settings",
        title: t("sidebar.nav.settings"),
        icon: Settings,
        path: "/settings",
        description: t("sidebar.nav.settingsDesc"),
      },
    ],
  },
];
